import {Injectable, Injector, OnDestroy} from '@angular/core';
import {Project} from '@docma-mm-core/project/project';
import {BehaviorSubject, combineLatest, defer, filter, firstValueFrom, map, Observable} from 'rxjs';
import {MmFilterModel, FieldFilter} from './mm-filter.model';
import {Layout} from '@docma-mm-core/layout/layout';
import {MmViewService} from '../mm-view.service';
import {flattenStructure, Structure} from '@docma-mm-core/structure/structure';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {PersonalizationKeysEnum} from '@docma-mm-core//personalization/personalization-keys.enum';
import {MmProjectDownloadService} from '../mm-project/mm-download.service';
import {MmFilterTag} from './tag/mm-filter-tag';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {ProjectStructure, ProjectStructureService, ProjectStructureTree} from '@mydocma/project';
import {Pool} from '../mm-common/pool/pool';
import {RecentlyUsedPoolsService} from './pool/services/recently-used-pools.service';

@Injectable()
export class MmFilterService implements OnDestroy {

  private stateSubject = new BehaviorSubject<MmFilterModel>(new MmFilterModel());
  public state = this.stateSubject.asObservable();

  constructor(private readonly mmViewService: MmViewService,
              private readonly mmDownloadService: MmProjectDownloadService,
              private readonly mmPersonalizationService: PersonalizationService,
              private readonly mmProjectLoaderService: MmProjectLoaderService,
              private readonly projectStructureService: ProjectStructureService,
              private readonly recentlyUsedPoolsService: RecentlyUsedPoolsService,
              ) {

    combineLatest([this.mmViewService.currentProject$, this.mmProjectLoaderService.structures.pipe(filter(st => st.loadState.status === LoadStatusEnum.Loaded))])
      .subscribe(async ([p, structures]) => {
        if (structures.data[0]?.Id === p?.Structure?.Id) {
          this.initialize(p).subscribe();
        }
      });
  }

  ngOnDestroy(): void {

  }

  initialize(project: Project): Observable<MmFilterModel> {
    return defer( async () => {
      const model = new MmFilterModel();
      if (project) {
        model.project = project;
        model.layout = await this.mmPersonalizationService
        .getProjectValue(project.MyDocmaUid, PersonalizationKeysEnum.LastLayoutId);
        // if (model.layout) {
        //   model.layout = await this.mmDownloadService.downloadLayout(project, model.layout.Id).toPromise();
        // }
        model.structure = await this.mmPersonalizationService.getProjectValue(project.MyDocmaUid, PersonalizationKeysEnum.LastStructureId);
        if (!model.structure) {
          model.structure = project.Structure;
        }
        else {
          const st = await firstValueFrom(this.mmProjectLoaderService.structures.pipe(filter(f => f.loadState.status === LoadStatusEnum.Loaded), map(f => flattenStructure(f.data).find(f => f.Id === model.structure.Id))));
          model.structure.Name2 = st?.Name2;

          this.setProjectStructure(model.structure);
        }
      }

      if (!model.fieldFilters){
        model.fieldFilters = [];
      }
      this.stateSubject.next(model);
      return model;
    });

  }

  setTags(tags: MmFilterTag[]) {
    const model = new MmFilterModel(this.stateSubject.value);
    model.tags = tags;
    this.stateSubject.next(model);
  }

  setPoolExclusionFilters(pools: Pool[]) {
    const model = new MmFilterModel(this.stateSubject.value);
    model.poolExclusionFilters = pools;
    this.stateSubject.next(model);
  }

  setPoolInclusionFilters(pools: Pool[]) {
    const model = new MmFilterModel(this.stateSubject.value);
    model.poolInclusionFilters = pools;
    this.stateSubject.next(model);
  }

  addPoolExclusionFilter(pool: Pool) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (!model.poolExclusionFilters) {
      model.poolExclusionFilters = [];
    }

    let stateChanged = false;
    const existingIndex = model.poolExclusionFilters.findIndex(p => p.Id === pool.Id);
    const existingInclusionIndex = model.poolInclusionFilters?.findIndex(p => p.Id === pool.Id);

    if (existingInclusionIndex !== -1) {
      model.poolInclusionFilters = model.poolInclusionFilters?.filter(p => p.Id !== pool.Id);
      stateChanged = true;
    }

    if (existingIndex === -1) {
      model.poolExclusionFilters.push(pool);
      stateChanged = true;
      this.recentlyUsedPoolsService.addRecentlyUsedPool(pool.Id);
    } else {
      console.warn('Pool already exists, not adding');
    }

    if (stateChanged) {
      this.stateSubject.next(model);
    }
  }

  removePoolExclusionFilter(poolId: number) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (model.poolExclusionFilters) {
      model.poolExclusionFilters = model.poolExclusionFilters.filter(p => p.Id !== poolId);
      this.stateSubject.next(model);
    }
  }

  addPoolInclusionFilter(pool: Pool) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (!model.poolInclusionFilters) {
      model.poolInclusionFilters = [];
    }

    let stateChanged = false;
    const existingIndex = model.poolInclusionFilters.findIndex(p => p.Id === pool.Id);
    const existingExclusionIndex = model.poolExclusionFilters?.findIndex(p => p.Id === pool.Id);

    if (existingExclusionIndex !== -1) {
      model.poolExclusionFilters = model.poolExclusionFilters?.filter(p => p.Id !== pool.Id);
      stateChanged = true;
    }

    if (existingIndex === -1) {
      model.poolInclusionFilters.push(pool);
      stateChanged = true;
      this.recentlyUsedPoolsService.addRecentlyUsedPool(pool.Id);
    } else {
      console.warn('Pool already exists, not adding');
    }

    if (stateChanged) {
      this.stateSubject.next(model);
    }
  }

  removePoolInclusionFilter(poolId: number) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (model.poolInclusionFilters) {
      model.poolInclusionFilters = model.poolInclusionFilters.filter(p => p.Id !== poolId);
      this.stateSubject.next(model);
    }
  }

  addFieldFilter(fieldName: string, value: string, displayValue: string) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (!model.fieldFilters) {
      model.fieldFilters = [];
    }

    const existingIndex = model.fieldFilters.findIndex(f => f.fieldName === fieldName);
    const fieldFilter: FieldFilter = { fieldName, value, displayValue };

    if (existingIndex === -1) {
      model.fieldFilters.push(fieldFilter);
    } else {
      model.fieldFilters[existingIndex] = fieldFilter;
    }

    this.stateSubject.next(model);
  }

  removeFieldFilter(fieldName: string) {
    const model = new MmFilterModel(this.stateSubject.value);
    if (model.fieldFilters) {
      model.fieldFilters = model.fieldFilters.filter(f => f.fieldName !== fieldName);
      this.stateSubject.next(model);
    }
  }

  setFieldFilters(fieldFilters: FieldFilter[]) {
    const model = new MmFilterModel(this.stateSubject.value);
    model.fieldFilters = fieldFilters;
    this.stateSubject.next(model);
  }

  // private compareProjects(p1, p2) {
  //   const id1 = p1 && p1.Id ? p1.Id : null;
  //   const id2 = p2 && p2.Id ? p2.Id : null;

  //   return id1 === id2;
  // }

  setLayout(layout: Layout) {
      // const model = new MmFilterModel(this.stateSubject.value);
      // if (model.project  && layout &&  !isIdsEquals(model.layout, layout.Id) ) {
      //   this.mmDownloadService.downloadLayout(model.project, layout.Id)
      //   .pipe(
      //     switchMap(l => from(this.mmPersonalizationService
      //       .setProjectValue(model.project.MyDocmaUid, PersonalizationKeysEnum.LastLayoutId, {Id: layout.Id, Name: layout.Name}))
      //       .pipe(map(ps => l)))
      //   )
      //   .subscribe((l: Layout) => {
      //     model.layout = l;
      //     this.stateSubject.next(model);
      //   });

      // }

      // return layout;
  }

  setStructure(structure: Structure) {

      const model = new MmFilterModel(this.stateSubject.value);
      model.structure = structure;
      if (model.project) {
        this.mmPersonalizationService
        .setProjectValue(model.project.MyDocmaUid, PersonalizationKeysEnum.LastStructureId, {Id: structure.Id, Name1: structure.Name1, RootId: structure.RootId, Parent: {Id: structure?.Parent?.Id}});

      }

      this.setProjectStructure(structure);
      this.stateSubject.next(model);
      return structure;

  }


  get filterState() {
    return this.stateSubject.value;
  }

  private setProjectStructure(structure: Structure) {
    if (!!this.projectStructureService.currentProjectStructureTree) {
      const bpStructure: ProjectStructure = ProjectStructureTree.getNodeById(this.projectStructureService.currentProjectStructureTree, structure.Id.toString());
      if (!bpStructure) { this.projectStructureService.currentProjectStructureTree.id = this.projectStructureService.currentProjectStructureTree.projectId; }
      this.projectStructureService.currentProjectStructure = bpStructure ?? this.projectStructureService.currentProjectStructureTree;
    }
  }

  clearAllFilters() {
    this.setPoolExclusionFilters([]);
    this.setPoolInclusionFilters([]);
    this.setFieldFilters([]);
    this.setStructure(this.filterState.project.Structure);
  }
}
